import { Fragment } from 'react';

import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordSingular } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { BuildingStoreIcon } from '@zola/zola-ui/src/components/SvgIconsV3/BuildingStore';
import { CalendarIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Calendar';
import { CircleDollarIcon } from '@zola/zola-ui/src/components/SvgIconsV3/CircleDollar';
import { MapPinIcon } from '@zola/zola-ui/src/components/SvgIconsV3/MapPin';
import { UsersIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Users';

import _capitalize from 'lodash/capitalize';
import { useFormState } from 'react-final-form';

import { ExpandedWeddingDateSummaryView } from '~/components/inquiries/ExpandedWeddingDates/ExpandedWeddingDateSummaryView';

import { ConditionalCheckmark } from '../../ConditionalCheckmark/ConditionalCheckmark';
import { ExpandedDateFields } from '../../form/ExpandedDateFields/ExpandedDateFields';
import { isExpandedDateAnswered } from '../../form/ExpandedDateFields/isExpandedDateAnswered';
import { validateExpandedWeddingDates } from '../../form/ExpandedDateFields/validateExpandedWeddingDates';
import { NumberFieldWithFlexibility } from '../../form/NumberFieldWithFlexibility/NumberFieldWithFlexibility';
import {
  WeddingLocationInput,
  isAnswered as isLocationProvided,
} from '../../form/WeddingLocationInput/WeddingLocationInput';
import { formatNumberWithCommas } from '../../zolaUI/NumberInputWithCommas/NumberInputWithCommas';
import MiniQuizVendorQuestion from './components/MiniQuizVendorQuestion/MiniQuizVendorQuestion';
import { MiniQuizQuestion, MiniQuizFormValues, MiniQuiz } from './miniQuizTypes';

type MiniQuizQuestionMeta = (Omit<MiniQuizQuestion, 'heading'> & {
  heading: string | ((taxonomyKey: SearchableVendorTaxonomyKey) => string);
})[];

const LocationSummary = (): JSX.Element => {
  const { values } = useFormState<MiniQuizFormValues>();

  return (
    <Fragment>
      {values.weddingLocation?.city}, {values.weddingLocation?.stateProvince}
    </Fragment>
  );
};

const isNumberFieldAnswered =
  (name: 'weddingEndPrice' | 'serviceEndPrice' | 'guestCount') => (values: MiniQuizFormValues) => {
    return Number(values[name]) > 0;
  };

type BudgetFieldProps =
  | {
      name: 'weddingEndPrice';
      flexibleFieldName: 'flexibleOnWeddingPrice';
    }
  | {
      name: 'serviceEndPrice';
      flexibleFieldName: 'flexibleOnPrice';
    };

const BudgetField = ({ name, flexibleFieldName }: BudgetFieldProps) => {
  return (
    <NumberFieldWithFlexibility
      name={name}
      label={undefined}
      placeholder="Enter a number"
      data-testid={name}
      type="currency"
      required
      flexibleFieldName={flexibleFieldName}
    />
  );
};

const NumberWithFlexibility: React.FC<{ flexible: boolean }> = ({ children, flexible }) => {
  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {children}
      <ConditionalCheckmark visible={flexible} style={{ margin: 0, marginLeft: '16px' }}>
        This is flexible
      </ConditionalCheckmark>
    </span>
  );
};

const BudgetSummary = ({ name, flexibleFieldName }: BudgetFieldProps): JSX.Element => {
  const { values } = useFormState<MiniQuizFormValues>();

  return (
    <NumberWithFlexibility flexible={Boolean(values[flexibleFieldName])}>
      ${formatNumberWithCommas(values[name])}
    </NumberWithFlexibility>
  );
};

const GuestCountField = () => {
  return (
    <NumberFieldWithFlexibility
      name="guestCount"
      label={undefined}
      placeholder="Enter a number"
      data-testid="guestCount"
      type="number"
      required
      flexibleFieldName="flexibleOnGuestCount"
    />
  );
};

const GuestCountSummary = (): JSX.Element => {
  const { values } = useFormState<MiniQuizFormValues>();

  return (
    <NumberWithFlexibility flexible={Boolean(values.flexibleOnGuestCount)}>
      {formatNumberWithCommas(values.guestCount)}
    </NumberWithFlexibility>
  );
};

const hasDefinedError = (validationResult: Record<string, any> | undefined) => {
  if (validationResult) {
    for (const key of Object.keys(validationResult)) {
      const value = validationResult[key];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (hasDefinedError(value)) {
          return true;
        }
      } else {
        return value !== undefined;
      }
    }
  }

  return false;
};

export const isWeddingDateAnswered = (values: MiniQuizFormValues): boolean => {
  const expandedAnswered = isExpandedDateAnswered(values);
  const validationResult = validateExpandedWeddingDates(values);

  return expandedAnswered && !hasDefinedError(validationResult);
};

const fullScreenMiniQuizEnabled = () =>
  window.location.pathname === '/wedding-vendors/invites/quiz';

export const isVendorCategoryAnswered = (values: MiniQuizFormValues): boolean => {
  return Boolean(values.vendorCategory);
};

const VendorCategorySummary = (): JSX.Element => {
  const { values } = useFormState<MiniQuizFormValues>();

  return <Fragment>{_capitalize(getVendorWordSingular(values.vendorCategory))}</Fragment>;
};

const vendorCategoryQuestion = {
  heading: 'Vendor type',
  slug: 'vendor-category',
  icon: <BuildingStoreIcon height={24} width={24} />,
  formComponent: () => <MiniQuizVendorQuestion />,
  summaryComponent: () => <VendorCategorySummary />,
  isAnswered: isVendorCategoryAnswered,
};

const MiniQuizMeta = (): MiniQuizQuestionMeta => {
  const meta = [
    {
      heading: 'Wedding date',
      slug: 'wedding-date',
      icon: <CalendarIcon height={24} width={24} />,
      formComponent: () => <ExpandedDateFields forceErrorsVisible />,
      summaryComponent: () => <ExpandedWeddingDateSummaryView />,
      isAnswered: isWeddingDateAnswered,
      formValidator: validateExpandedWeddingDates,
    },
    {
      heading: 'Wedding location',
      slug: 'wedding-location',
      icon: <MapPinIcon height={24} width={24} />,
      formComponent: () => <WeddingLocationInput />,
      summaryComponent: () => <LocationSummary />,
      isAnswered: isLocationProvided,
    },
    {
      heading: 'Overall wedding budget',
      slug: 'wedding-budget',
      icon: <CircleDollarIcon height={24} width={24} />,
      formComponent: () => (
        <BudgetField name="weddingEndPrice" flexibleFieldName="flexibleOnWeddingPrice" />
      ),
      summaryComponent: () => (
        <BudgetSummary name="weddingEndPrice" flexibleFieldName="flexibleOnWeddingPrice" />
      ),
      isAnswered: isNumberFieldAnswered('weddingEndPrice'),
    },
    {
      heading: (taxonomyKey: SearchableVendorTaxonomyKey) =>
        `${_capitalize(getVendorWordSingular(taxonomyKey))} budget`,
      slug: 'service-budget',
      icon: <CircleDollarIcon height={24} width={24} />,
      formComponent: () => (
        <BudgetField name="serviceEndPrice" flexibleFieldName="flexibleOnPrice" />
      ),
      summaryComponent: () => (
        <BudgetSummary name="serviceEndPrice" flexibleFieldName="flexibleOnPrice" />
      ),
      isAnswered: isNumberFieldAnswered('serviceEndPrice'),
    },
    {
      heading: 'Guest count',
      slug: 'guest-count',
      icon: <UsersIcon height={24} width={24} />,
      formComponent: () => <GuestCountField />,
      summaryComponent: () => <GuestCountSummary />,
      isAnswered: isNumberFieldAnswered('guestCount'),
    },
  ];

  if (fullScreenMiniQuizEnabled()) {
    meta.unshift(vendorCategoryQuestion);
  }
  return meta;
};

export const getMiniQuiz = (taxonomyKey: SearchableVendorTaxonomyKey): MiniQuiz => {
  return MiniQuizMeta().map((question) => {
    return {
      ...question,
      heading:
        typeof question.heading === 'function' ? question.heading(taxonomyKey) : question.heading,
    };
  });
};
